<template>
  <container class="catalog-page pb-20">
    <zaproo-breadcrumbs>
      <zaproo-breadcrumb
        v-for="breadcrumb in breadcrumbs"
        :key="breadcrumb.label"
        :to="breadcrumb.link"
      >
        {{ breadcrumb.label }}
      </zaproo-breadcrumb>
    </zaproo-breadcrumbs>

    <div class="py-10">
      <page-heading
        v-if="categoriesSearchStatus !== 'pending'"
        class="search-page-title !p-0"
      >
        {{ currentCategoryName }}
      </page-heading>
      <skeleton v-else class="h-[40px] max-w-[185px]" />

      <nuxt-lazy-hydrate>
        <lazy-catalog-category-description
          class="search-page-description"
          :description="category?.description"
        />
      </nuxt-lazy-hydrate>

      <related-special-order-button
        v-if="strapiCatalogCategorySettings?.special_order?.data?.attributes"
        :special-order="
          strapiCatalogCategorySettings.special_order.data.attributes
        "
        class="mt-5"
      />
    </div>

    <product-results-top-bar
      :products-qty="totalResultsFound"
      :filters-qty="selectedFilterCount"
      :has-filters-button="
        sidebarContentInstance
          ? !sidebarContentInstance?.hasAnySidebarOpened ||
            (isDown(mobileViewMaxWidth) && isUpper(Breakpoint.sm))
          : false
      "
      :filter="filters"
      :type="SearchProductType.catalogPage"
      :loading="pending"
      class="search-page-bar"
      @open-filter="sidebarContentInstance?.openSidebar()"
      @on-filter-change="onFilterChange"
    />

    <sidebar-content
      v-if="pending || !isProductsAndFiltersNoResults"
      ref="sidebarContentInstance"
      :mobile-sidebar-title="$t('Filters')"
      :mobile-view-max-width="mobileViewMaxWidth"
      :mobile-sidebar-position="OpenedSidebarPosition.left"
      mobile-sidebar-content-classes="!p-0"
      class="filters-sidebar lg:!top-[20px]"
    >
      <!--PAGE SIDEBAR-->

      <template #sidebar>
        <filters-sidebar
          v-show="!error"
          id="sidebar-content"
          :sidebar-content-int="sidebarContentInstance"
          :filter-manager="filters.filter"
          :applied-filter-manager="committedQueryVariables.filter"
          :mode="FiltersMode.catalog"
          :category="category"
          :loading="pending && !filters.filter?.filters.length"
          @on-apply-filters="onFilterSensitiveDataChange()"
        />
      </template>

      <zaproo-notification
        v-if="error"
        class="!w-full !max-w-none"
        :type="NotificationType.error"
        :closable="false"
        :title="$t('Something\'s wrong')"
      >
        {{ error.toString() }}
      </zaproo-notification>

      <catalog-scroll-anchor />

      <!--PAGE CONTENT-->
      <skeleton-dynamic-portal
        :content-loaded="!pending && categoriesSearchStatus !== 'pending'"
        :skeleton-component="ProductsGallerySkeleton"
        :items-per-page="productList?.length ?? 32"
      >
        <div v-if="productList.length" class="w-full">
          <products-gallery
            id="product-gallery"
            :products="productList"
            :extended-grid="
              !!(sidebarContentInstance?.hasDesktopSidebarOpened ?? true)
            "
            :analytics-data="{ list: `Catalog` }"
            :product-wrapper="
              isMagento
                ? undefined
                : KlevuEventCategoryMerchandisingProductClick
            "
            :product-wrapper-props="{
              categoryTitle: category?.name || '',
              breadcrumbs: breadcrumbs,
            }"
          />
          <divider class="md max-sm:my-[30px] max-sm:h-0 sm:my-10" />
          <div v-if="totalPages" class="flex w-full justify-center">
            <zaproo-pagination
              :total-pages="totalPages"
              :max-pages-visible="isDown(Breakpoint.sm) ? 1 : 3"
              :page="committedQueryVariables.page"
              @page-change="
                filters.page = $event;
                commitPush();
              "
            />
          </div>
        </div>
        <quick-search-products-not-found v-else class="my-10 flex-1" />
      </skeleton-dynamic-portal>
    </sidebar-content>

    <quick-search-products-not-found v-else class="my-10 flex-1" />

    <micro-schema-category :sort="filters.sort" :products="productList" />
    <micro-schema-breadcrumb-list :breadcrumbs="breadcrumbs" />
  </container>
</template>

<script lang="ts" setup>
import {
  NotificationType,
  OpenedSidebarPosition,
} from "@zaprooecommerce/storefront-ui-vue";
import { isEqual, set, truncate } from "lodash-es";
import { stripHtml } from "string-strip-html";
import ProductsGallerySkeleton from "../components/ProductsGallerySkeleton.vue";

import { CATALOG_PAGE_CMS_CATEGORY } from "../variables/CatalogFetchKeys";

import type { CategoryBreadcrumb } from "~/types/category/CategoryBreadcrumb";
import type { SidebarContentInstance } from "~/modules/helpers/components/SidebarContent/types";
import ProductsGallery from "~/components/catalog/products/ProductsGallery.vue";
import Divider from "~/components/general/Divider.vue";
import FiltersSidebar from "~/modules/catalog/components/FiltersSidebar.vue";
import RelatedSpecialOrderButton from "~/modules/catalog/components/RelatedSpecialOrderButton.vue";

import { FiltersMode } from "~/modules/catalog/components/Filter/types";
import { SearchProductType } from "~/modules/settings/types";
import { Breadcrumb } from "~/types/breadcrumbs";
import { SeoPageMeta } from "~/modules/seo-pack/types/SeoPageMeta";
import { mobileViewMaxWidth } from "~/modules/catalog/variables/mobileViewMaxWidth";
import KlevuEventCategoryMerchandisingProductClick from "~/modules/klevu/components/KlevuEventCategoryMerchandisingProductClick.vue";
import { useKlevuCatalogPageData } from "~/modules/catalog/composables/klevu/useKlevuCatalogPageData";
import { useMagentoCatalogPageData } from "~/modules/catalog/composables/magento-search/useMagentoCatalogPageData";

const { isMagento } = useSearchProvider();
const route = useRoute();
const { isDown, isUpper } = useViewportObserver();
const { fetchCatalogCategory } = useStrapiCatalogCategory();
const linker = useLinker();
const catalogLinker = useCatalogLinker();
const { t } = useI18n();
const { reset: resetFilterSidebarOpenedStatus } = useFilterSidebarOpenState();

const slugs = computed<string[]>(() => (route.params.slugs as string[]) || []);

const {
  category,
  categoriesSearchStatus,
  fetchCategories,
  categoriesSearchError,
} = await useCatalogCategoryResolver(slugs);

const categoryBreadcrumbs = useCategoryBreadcrumbs(category);
const currentCategoryName = computed<string>(() => category.value?.name ?? "");

const breadcrumbs = computed<Breadcrumb[]>(() => [
  { link: linker.home(), label: t("Home") },
  ...categoryBreadcrumbs.value.map((c: CategoryBreadcrumb) => ({
    link: catalogLinker.categoryLink(c),
    label: c.category_name,
  })),
]);

const sidebarContentInstance = ref<SidebarContentInstance | undefined>();

// Provider-specific data fetching
const catalogData = isMagento.value
  ? await useMagentoCatalogPageData(computed(() => category.value?.uid))
  : await useKlevuCatalogPageData(
      slugs,
      category,
      categoryBreadcrumbs,
      fetchCategories,
    );

const {
  filters,
  committedQueryVariables,
  commitPush,
  reset,
  pending,
  productList,
  totalResultsFound,
  isProductsAndFiltersNoResults,
  onFilterSensitiveDataChange,
  error: catalogError,
  refreshPage,
  totalPages,
  selectedFilterCount,
} = catalogData;

const error = computed<any | undefined>(
  () => catalogError.value || categoriesSearchError.value,
);

const { data: strapiCatalogCategorySettings } = useSyncCachedAsyncData(
  CATALOG_PAGE_CMS_CATEGORY,
  async () => {
    return category.value
      ? await fetchCatalogCategory(category.value!.uid)
      : undefined;
  },
  { watch: [category] },
);

function onFilterChange($event: { key: keyof typeof filters; val: any }): void {
  set(filters, $event.key, $event.val);
  onFilterSensitiveDataChange();
}

watch(slugs, (value, oldValue) => {
  if (!isEqual(value, oldValue)) {
    reset();
  }
});

watch(category, () => {
  resetFilterSidebarOpenedStatus();
});

watch(committedQueryVariables, () => {
  refreshPage();
});

const metaTitle = computed<string>(
  () => category.value?.meta_title ?? currentCategoryName.value ?? "",
);
const metaDescription = computed<string | undefined>(
  () =>
    category.value?.meta_description ??
    stripHtml(category.value?.description ?? "").result,
);

useSeoPack(
  computed(
    () =>
      ({
        title: metaTitle.value,
        description: truncate(metaDescription.value, {
          length: 150,
        }),
      }) as SeoPageMeta,
  ),
);

useCacheTagger((addCacheTags) => {
  if (categoryBreadcrumbs.value?.length) {
    addCacheTags(categoryBreadcrumbs.value?.map((c) => categoryTag(c)));
  }
});

useScrollTo(committedQueryVariables, "#scroll-anchor");
</script>

<style lang="scss" src="../assets/index.scss" />
